import { useState } from "react";

type Props = {
  value: string[] | null;
  onChange: (next: string[] | null) => void;
};

/**
 * Configure the sauce / add-on picker shown to customers on the dessert
 * order form. Empty list = no picker. Lets baristas adjust the menu's
 * available sauces (e.g. add a new flavour) without a code deploy.
 */
const SaucesEditor: React.FC<Props> = ({ value, onChange }) => {
  const list = value ?? [];
  const [draft, setDraft] = useState("");

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (list.includes(v)) { setDraft(""); return; }
    onChange([...list, v]);
    setDraft("");
  };

  const remove = (i: number) => {
    const next = list.filter((_, j) => j !== i);
    onChange(next.length > 0 ? next : null);
  };

  return (
    <div className="rounded-2xl bg-ink-50 p-4 space-y-3">
      {list.length === 0 ? (
        <p className="text-xs text-ink-500">
          No sauces configured — customers won't see a picker. Add one below to enable it.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {list.map((s, i) => (
            <span
              key={`${s}-${i}`}
              className="inline-flex items-center gap-1.5 pl-3 pr-1.5 h-[34px] rounded-full bg-white border border-ink-200 text-sm font-medium text-ink-1000"
            >
              {s}
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label={`Remove ${s}`}
                className="w-6 h-6 rounded-full hover:bg-ink-100 flex items-center justify-center text-ink-500 hover:text-ink-1000"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); add(); }
          }}
          placeholder="e.g. Thai tea, Fresh milk"
          maxLength={100}
          className="flex-1 h-[44px] px-4 rounded-xl bg-white border-2 border-transparent text-sm text-ink-1000 placeholder:text-ink-400 focus:outline-none focus:border-ink-1000 transition-colors"
        />
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim()}
          className="px-4 h-[44px] rounded-xl bg-ink-1000 text-ink-0 text-sm font-bold disabled:opacity-40"
        >
          Add
        </button>
      </div>
      <p className="text-[11px] text-ink-500">
        Tip: ใส่ "ไม่เอาซอส" ถ้าอยากให้ลูกค้าเลือกไม่ใส่ซอสได้
      </p>
    </div>
  );
};

export default SaucesEditor;
